import { useEffect, useMemo, useRef, useState } from "react";

import { usePageTitle } from "@/hooks/usePageTitle";
import {
  filterImportedIntegrators,
  formatImportedIntegratorSyncDate,
  importedIntegratorEnvironmentLabel,
  importedIntegratorOriginLabel,
  importedIntegratorsResultRangeLabel,
  importedIntegratorSyncLabel,
  listAdminImportedIntegrators,
  summarizeImportedIntegrators,
  type AdminImportedIntegrator,
  type ImportedIntegratorFilter,
  type ImportedIntegratorSyncStatus,
} from "@/lib/api/admin-imported-integrators";
import { AdminShell } from "@/pages/admin/AdminShell";

const SEARCH_DEBOUNCE_MS = 250;

type LoadState =
  | { kind: "loading" }
  | { kind: "refreshing" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

const FILTERS: { id: ImportedIntegratorFilter; label: string; hint: string }[] =
  [
    { id: "all", label: "All", hint: "imported links" },
    { id: "sandbox", label: "Sandbox", hint: "sandbox environment" },
    { id: "live", label: "Live", hint: "live environment" },
    { id: "sync_failed", label: "Sync failed", hint: "needs a retry" },
  ];

function syncBadgeClass(status: ImportedIntegratorSyncStatus): string {
  if (status === "linked") return "s-approved";
  if (status === "failed") return "s-denied";
  return "s-review";
}

export default function AdminImportedIntegratorsPage() {
  usePageTitle("Imported integrators — API Admin Panel");
  const [rows, setRows] = useState<AdminImportedIntegrator[]>([]);
  const [filter, setFilter] = useState<ImportedIntegratorFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const appliedSearchRef = useRef(debouncedSearch);

  useEffect(() => {
    appliedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = search.trim();
      if (next === appliedSearchRef.current) {
        return;
      }
      setDebouncedSearch(next);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      await Promise.resolve();
      if (cancelled) {
        return;
      }

      setLoadState((prev) =>
        prev.kind === "ready" || prev.kind === "refreshing"
          ? { kind: "refreshing" }
          : { kind: "loading" },
      );

      try {
        const response = await listAdminImportedIntegrators();
        if (cancelled) {
          return;
        }
        setRows(response.integrators);
        setLoadState({ kind: "ready" });
      } catch {
        if (cancelled) {
          return;
        }
        setRows([]);
        setLoadState({
          kind: "error",
          message: "Could not load imported integrators. Try again.",
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const summary = useMemo(() => summarizeImportedIntegrators(rows), [rows]);
  const visibleRows = useMemo(
    () =>
      filterImportedIntegrators(rows, filter, {
        query: debouncedSearch,
      }),
    [rows, filter, debouncedSearch],
  );
  const listBusy = loadState.kind === "loading" || loadState.kind === "refreshing";
  const hasActiveFilters = debouncedSearch.length > 0 || filter !== "all";

  function clearFilters() {
    setFilter("all");
    setSearch("");
    setDebouncedSearch("");
  }

  function countFor(id: ImportedIntegratorFilter): number {
    if (id === "sandbox") return summary.sandbox;
    if (id === "live") return summary.live;
    if (id === "sync_failed") return summary.syncFailed;
    return summary.total;
  }

  return (
    <AdminShell
      activeNav="imported-integrators"
      title="Imported integrators"
      description="Sandbox and live integrators already held in Glofox. Hand-submitted access requests stay on Access requests."
      testId="admin-imported-integrators-page"
    >
      <div
        className="sum-row"
        role="group"
        aria-label="Filter imported integrators"
        data-testid="admin-imported-integrators-filters"
      >
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`sum-card${filter === item.id ? " on" : ""}`}
            aria-pressed={filter === item.id}
            data-testid={`admin-imported-integrators-filter-${item.id}`}
            onClick={() => setFilter(item.id)}
          >
            <div className="sum-lbl">{item.label}</div>
            <div
              className={`sum-val${
                item.id === "sync_failed" ? " sum-val--denied" : ""
              }`}
              data-testid={`admin-imported-integrators-count-${item.id}`}
            >
              {countFor(item.id)}
            </div>
            <div className="sum-sub">{item.hint}</div>
          </button>
        ))}
      </div>

      <div
        className="mobile-status"
        role="group"
        aria-label="Filter imported integrators"
        data-testid="admin-imported-integrators-mobile-filters"
      >
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`mobile-status-btn${filter === item.id ? " on" : ""}`}
            aria-pressed={filter === item.id}
            data-testid={`admin-imported-integrators-mobile-filter-${item.id}`}
            onClick={() => setFilter(item.id)}
          >
            {item.label} <span>{countFor(item.id)}</span>
          </button>
        ))}
      </div>

      <div className="toolbar" data-testid="admin-imported-integrators-toolbar">
        <div className="search-wrap">
          <i className="ti ti-search" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, email, company or integrator ID…"
            aria-label="Search imported integrators"
            data-testid="admin-imported-integrators-search"
          />
        </div>
        <button
          type="button"
          className={`filter-btn${loadState.kind === "refreshing" ? " is-busy" : ""}`}
          data-testid="admin-imported-integrators-refresh"
          aria-label="Refresh imported integrators"
          disabled={listBusy}
          onClick={() => setReloadKey((key) => key + 1)}
        >
          <i
            className={`ti ${listBusy ? "ti-loader-2" : "ti-refresh"}`}
            aria-hidden="true"
          />
          {loadState.kind === "refreshing" ? "Updating" : "Refresh"}
        </button>
      </div>

      <div className="results-meta" data-testid="admin-imported-integrators-results-meta">
        <span>{importedIntegratorsResultRangeLabel(visibleRows.length)}</span>
        {hasActiveFilters ? (
          <button
            type="button"
            className="results-clear"
            data-testid="admin-imported-integrators-clear-filters"
            onClick={clearFilters}
          >
            Clear filters
          </button>
        ) : null}
      </div>

      <div className="tbl-wrap">
        {loadState.kind === "loading" ? (
          <div className="empty-state" data-testid="admin-imported-integrators-loading">
            <i className="ti ti-loader-2" aria-hidden="true" />
            <p>Loading imported integrators…</p>
          </div>
        ) : null}

        {loadState.kind === "error" ? (
          <div className="empty-state" data-testid="admin-imported-integrators-error">
            <i className="ti ti-alert-circle" aria-hidden="true" />
            <p>{loadState.message}</p>
            <button
              type="button"
              className="filter-btn on"
              onClick={() => setReloadKey((value) => value + 1)}
            >
              Retry
            </button>
          </div>
        ) : null}

        {loadState.kind === "ready" && visibleRows.length === 0 ? (
          <div className="empty-state" data-testid="admin-imported-integrators-empty">
            <i className="ti ti-link" aria-hidden="true" />
            <p>
              {hasActiveFilters
                ? "No imported integrators match the current filters."
                : "No imported integrators yet."}
            </p>
          </div>
        ) : null}

        {(loadState.kind === "ready" || loadState.kind === "refreshing") &&
        visibleRows.length > 0 ? (
          <div
            className={`tbl-scroll${loadState.kind === "refreshing" ? " is-refreshing" : ""}`}
            data-testid="admin-imported-integrators-list"
            role="region"
            tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex -- overflow lists must take keyboard focus
            aria-label="Imported integrators"
            aria-busy={listBusy}
          >
            {loadState.kind === "refreshing" ? (
              <div
                className="tbl-refresh"
                data-testid="admin-imported-integrators-refreshing"
                role="status"
                aria-live="polite"
              >
                <i className="ti ti-loader-2" aria-hidden="true" />
                <span>Updating results…</span>
              </div>
            ) : null}
            <table className="req-table imported-integrators-table">
              <thead>
                <tr>
                  <th scope="col">Applicant</th>
                  <th scope="col">Environment</th>
                  <th scope="col">Integrator ID</th>
                  <th scope="col">Origin</th>
                  <th scope="col">Sync</th>
                  <th scope="col">Last sync</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr
                    key={row.id}
                    className="req-tr req-tr--static"
                    data-testid={`admin-imported-integrator-row-${row.id}`}
                  >
                    <td>
                      <div className="req-name">{row.contact_name}</div>
                      <div className="req-email">{row.email}</div>
                      <div className="req-biz">{row.company_name}</div>
                    </td>
                    <td>{importedIntegratorEnvironmentLabel(row.key_environment)}</td>
                    <td>
                      <code
                        className="request-id-code"
                        data-testid={`admin-imported-integrator-id-${row.id}`}
                      >
                        {row.integrator_id}
                      </code>
                    </td>
                    <td>{importedIntegratorOriginLabel()}</td>
                    <td>
                      <span className={`status-badge ${syncBadgeClass(row.sync_status)}`}>
                        <span className="s-dot" />
                        {importedIntegratorSyncLabel(row.sync_status)}
                      </span>
                    </td>
                    <td>
                      <div className="req-date">
                        {formatImportedIntegratorSyncDate(row.last_sync_at)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </AdminShell>
  );
}
